/**
 * Viewport controls for PowerEditor - handles device selection and path navigation
 */

import { useMemo } from "react";
import {
  SmartphoneIcon,
  Laptop2Icon,
  MonitorIcon,
  Presentation,
  RectangleVertical,
  ExternalLink,
  RefreshCw,
  Maximize2,
  Check,
  AlertTriangle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Hint } from "@/components/ui/hint";
import { ViewportSize, VIEWPORT_SIZES } from "@/types/viewport-device.types";
import { PowerEditorViewportControlsProps } from "@/types/power-editor.types";
import { useResponsive } from "@/hooks/useResponsive";

export default function PowerEditorViewportControls({
  isResponsiveMode,
  viewportSize,
  isCustomViewport,
  customWidth,
  customHeight,
  currentPath,
  availablePaths,
  showDeviceDropdown,
  showPathDropdown,
  showPreview,
  onResponsiveModeChange,
  onViewportSizeChange,
  onCustomViewportChange,
  onPathSelect,
  onToggleDeviceDropdown,
  onTogglePathDropdown,
  onReloadPreview,
  onOpenPreviewInNewWindow,
  customClassNameWidth,
}: PowerEditorViewportControlsProps): JSX.Element {
  // Viewport display configuration
  const viewportConfig = useMemo(() => {
    const configs = {
      [ViewportSize.mobile]: {
        icon: SmartphoneIcon,
        label: "Mobile",
        dimensions: "375×667",
      },
      [ViewportSize.tablet]: {
        icon: Laptop2Icon,
        label: "Tablet",
        dimensions: "768×1024",
      },
      [ViewportSize.desktop]: {
        icon: MonitorIcon,
        label: "Desktop",
        dimensions: "1024×768",
      },
      [ViewportSize.signageLandscape]: {
        icon: Presentation,
        label: "Signage Landscape",
        dimensions: "1920×1080",
      },
      [ViewportSize.signagePortrait]: {
        icon: RectangleVertical,
        label: "Signage Portrait",
        dimensions: "1080×1920",
      },
    };
    return configs[viewportSize] || configs[ViewportSize.desktop];
  }, [viewportSize]);

  const { isMobilePowerEditor } = useResponsive();

  const handleCustomWidthChange = (value: number) => {
    onCustomViewportChange(value, customHeight);
    onResponsiveModeChange(true);
  };

  const handleCustomHeightChange = (value: number) => {
    onCustomViewportChange(customWidth, value);
    onResponsiveModeChange(true);
  };

  return (
    <div
      className={cn(
        "flex items-center bg-card border border-border rounded-lg h-8 px-3",
        customClassNameWidth || "min-w-[220px]"
      )}
    >
      {/* Device Selector Dropdown */}
      <div className="relative flex items-center mr-1">
        {!isMobilePowerEditor && (
          <Hint
            delayDuration={0}
            text="Change viewport"
            side="bottom"
            align="center"
          >
            <button
              onClick={() => {
                onToggleDeviceDropdown();
                if (!showPreview) onResponsiveModeChange(true);
              }}
              className="flex items-center p-1 rounded hover:bg-accent transition-colors"
            >
              {isCustomViewport ? (
                <MonitorIcon className="w-4 h-4" />
              ) : (
                <viewportConfig.icon className="w-4 h-4" />
              )}
            </button>
          </Hint>
        )}
        {/* Dropdown Menu */}
        {showDeviceDropdown && (
          <>
            <div
              className="fixed inset-0 z-40"
              onClick={onToggleDeviceDropdown}
            />
            <div className="absolute top-full left-0 mt-2 bg-popover border border-border rounded-lg shadow-lg py-1 z-50 min-w-[200px]">
              {/* Full Size option */}
              <button
                onClick={() => {
                  onResponsiveModeChange(false);
                  onToggleDeviceDropdown();
                }}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-accent transition-colors",
                  !isResponsiveMode && "bg-accent"
                )}
              >
                <Maximize2 className="w-4 h-4" />
                <span className="flex-1 text-left">Full Size</span>
                <span className="text-xs text-muted-foreground">
                  Fill space
                </span>
              </button>
              <div className="border-t border-border my-1" />

              {/* Mobile */}
              <button
                onClick={() => {
                  onViewportSizeChange(ViewportSize.mobile);
                  onCustomViewportChange(
                    VIEWPORT_SIZES.mobile.width as number,
                    VIEWPORT_SIZES.mobile.height as number
                  );
                  onResponsiveModeChange(true);
                  onToggleDeviceDropdown();
                }}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-accent transition-colors",
                  isResponsiveMode &&
                    viewportSize === ViewportSize.mobile &&
                    !isCustomViewport &&
                    "bg-accent"
                )}
              >
                <SmartphoneIcon className="w-4 h-4" />
                <span className="flex-1 text-left">Mobile</span>
                <span className="text-xs text-muted-foreground">375×667</span>
              </button>

              {/* Tablet */}
              <button
                onClick={() => {
                  onViewportSizeChange(ViewportSize.tablet);
                  onCustomViewportChange(
                    VIEWPORT_SIZES.tablet.width as number,
                    VIEWPORT_SIZES.tablet.height as number
                  );
                  onResponsiveModeChange(true);
                  onToggleDeviceDropdown();
                }}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-accent transition-colors",
                  isResponsiveMode &&
                    viewportSize === ViewportSize.tablet &&
                    !isCustomViewport &&
                    "bg-accent"
                )}
              >
                <Laptop2Icon className="w-4 h-4" />
                <span className="flex-1 text-left">Tablet</span>
                <span className="text-xs text-muted-foreground">768×1024</span>
              </button>

              {/* Desktop */}
              <button
                onClick={() => {
                  onViewportSizeChange(ViewportSize.desktop);
                  onCustomViewportChange(
                    VIEWPORT_SIZES.desktop.width as number,
                    VIEWPORT_SIZES.desktop.height as number
                  );
                  onResponsiveModeChange(true);
                  onToggleDeviceDropdown();
                }}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-accent transition-colors",
                  isResponsiveMode &&
                    viewportSize === ViewportSize.desktop &&
                    !isCustomViewport &&
                    "bg-accent"
                )}
              >
                <MonitorIcon className="w-4 h-4" />
                <span className="flex-1 text-left">Desktop</span>
                <span className="text-xs text-muted-foreground">1024×768</span>
              </button>

              {/* Signage Landscape */}
              <button
                onClick={() => {
                  onViewportSizeChange(ViewportSize.signageLandscape);
                  onCustomViewportChange(
                    VIEWPORT_SIZES.signageLandscape.width as number,
                    VIEWPORT_SIZES.signageLandscape.height as number
                  );
                  onResponsiveModeChange(true);
                  onToggleDeviceDropdown();
                }}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-accent transition-colors",
                  isResponsiveMode &&
                    viewportSize === ViewportSize.signageLandscape &&
                    !isCustomViewport &&
                    "bg-accent"
                )}
              >
                <Presentation className="w-4 h-4" />
                <span className="flex-1 text-left">Signage Landscape</span>
                <span className="text-xs text-muted-foreground">1920×1080</span>
              </button>

              {/* Signage Portrait */}
              <button
                onClick={() => {
                  onViewportSizeChange(ViewportSize.signagePortrait);
                  onCustomViewportChange(
                    VIEWPORT_SIZES.signagePortrait.width as number,
                    VIEWPORT_SIZES.signagePortrait.height as number
                  );
                  onResponsiveModeChange(true);
                  onToggleDeviceDropdown();
                }}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-accent transition-colors",
                  isResponsiveMode &&
                    viewportSize === ViewportSize.signagePortrait &&
                    !isCustomViewport &&
                    "bg-accent"
                )}
              >
                <RectangleVertical className="w-4 h-4" />
                <span className="flex-1 text-left">Signage Portrait</span>
                <span className="text-xs text-muted-foreground">1080×1920</span>
              </button>

              <div className="border-t border-border my-1" />

              {/* Custom size inputs */}
              <div className="px-3 py-2">
                <div className="flex items-center justify-center gap-2">
                  <input
                    type="number"
                    value={customWidth}
                    onChange={(e) => {
                      const val = parseInt(e.target.value) || 0;
                      handleCustomWidthChange(val);
                    }}
                    onFocus={() => {
                      onResponsiveModeChange(true);
                    }}
                    placeholder="W"
                    className="w-16 px-2 py-1 text-xs bg-background border border-border rounded text-center focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                  <span className="text-muted-foreground text-xs">×</span>
                  <input
                    type="number"
                    value={customHeight}
                    onChange={(e) => {
                      const val = parseInt(e.target.value) || 0;
                      handleCustomHeightChange(val);
                    }}
                    onFocus={() => {
                      onResponsiveModeChange(true);
                    }}
                    placeholder="H"
                    className="w-16 px-2 py-1 text-xs bg-background border border-border rounded text-center focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Path display - takes up most space */}
      <div className="relative flex items-center flex-1 mx-4">
        <Hint delayDuration={0} text="Change page" side="bottom" align="center">
          <button
            onClick={onTogglePathDropdown}
            className="flex items-center px-2 py-1 rounded hover:bg-accent transition-colors text-[13px]"
          >
            <span className="truncate">{currentPath}</span>
          </button>
        </Hint>

        {/* Path Dropdown Menu */}
        {showPathDropdown && (
          <>
            <div
              className="fixed inset-0 z-40"
              onClick={onTogglePathDropdown}
            />
            <div className="absolute top-full left-0 mt-2 bg-popover border border-border rounded-lg shadow-lg py-1 z-50 min-w-[180px] max-h-[300px] overflow-y-auto">
              {availablePaths.length > 0 ? (
                availablePaths.map((page) => (
                  <button
                    key={page.route}
                    onClick={() => onPathSelect(page.route)}
                    className={cn(
                      "w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-accent transition-colors",
                      currentPath === page.route && "bg-accent"
                    )}
                  >
                    <span className="flex items-center gap-1.5 text-left">
                      {page.route}
                      {!page.registered && (
                        <span title="Route not registered in App.tsx">
                          <AlertTriangle className="w-3.5 h-3.5 text-yellow-500" />
                        </span>
                      )}
                    </span>
                    {currentPath === page.route && (
                      <Check className="w-4 h-4 text-primary shrink-0" />
                    )}
                  </button>
                ))
              ) : (
                <div className="py-3 px-3 text-sm text-muted-foreground text-center">
                  No routes found
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Action buttons */}
      <Hint
        delayDuration={0}
        text="Open in new tab"
        side="bottom"
        align="center"
      >
        <button
          onClick={onOpenPreviewInNewWindow}
          className="p-1 rounded hover:bg-accent transition-colors mr-1"
        >
          <ExternalLink className="w-4 h-4" />
        </button>
      </Hint>
      <Hint
        delayDuration={0}
        text="Refresh preview"
        side="bottom"
        align="center"
      >
        <button
          onClick={onReloadPreview}
          className="p-1 rounded hover:bg-accent transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </Hint>
    </div>
  );
}
